import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Box,
  Button,
} from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useSupervisors } from "../hooks/useSupervisors";
import { useAgentSelection } from "../hooks/useAgentSelection";
import { usePagination } from "../hooks/usePagination";
import { useSummaryData } from "../hooks/useSummaryData";
import { useSummaryGeneration } from "../hooks/useSummaryGeneration";
import { useSelectionPersistence } from "../hooks/useSelectionPersistence";
import { useSplitPanel } from "../hooks/SplitPanelContext";
import SummaryConfigurationForm from "../components/insights/SummaryConfigurationForm";
import SummaryTable from "../components/insights/SummaryTable";
import ConfirmationModal from "../components/insights/ConfirmationModal";
import ResultModal from "../components/insights/ResultModal";
import InsightDetailPanel from "../components/insights/InsightDetailPanel";
import { DEFAULT_PREFERENCES, DEFAULT_SORTING_COLUMN, sanitizePreferences } from "../constants/insightsConstants";
import { useLocalStorage } from "../common/localStorage";
import * as api from "../api/api";
import { hasSearchText, isAllFilteredAvailable } from "../utils/insights/allFilteredMode";


function InsightsCalls() {
  const { t } = useTranslation();
  const history = useHistory();
  const { openSplitPanel, closeSplitPanel, splitPanelOpen, handleSplitPanelToggle } = useSplitPanel();

  // Estados locales mínimos
  const [summaryName, setSummaryName] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const selection = useSelectionPersistence();
  const [filteringText, setFilteringText] = useState("");
  // feature 025 (US2, T047): tamaño de página recordado por navegador, validado al leerlo
  const [storedPreferences, setPreferences] = useLocalStorage('insights-calls-preferences', DEFAULT_PREFERENCES);
  const preferences = useMemo(() => sanitizePreferences(storedPreferences), [storedPreferences]);
  const [sortingColumn, setSortingColumn] = useState(DEFAULT_SORTING_COLUMN);
  const [sortingDescending, setSortingDescending] = useState(true);

  // Estados para el panel de insight
  const [selectedCall, setSelectedCall] = useState(null);
  const [activeTabId, setActiveTabId] = useState("info");
  const [callDetails, setCallDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [insightError, setInsightError] = useState(null);

  // Callback para limpiar el supervisor cuando se cargan supervisores (nuevo rango). Los asesores
  // no se tocan acá: su reinicio y su reconciliación viven en useAgentSelection.
  const handleSupervisorsLoaded = React.useCallback((supervisor) => {
    setSelectedSupervisor(supervisor);
  }, []);

  // Hooks personalizados
  const supervisors = useSupervisors(dateRange, handleSupervisorsLoaded);
  // SS-537: selección de asesores (todos tildados por defecto; ver hooks/useAgentSelection)
  const { agents, selectedAgents, setSelectedAgents, effectiveAgents, agentsNone } =
    useAgentSelection(dateRange, selectedSupervisor, t);

  const pagination = usePagination(dateRange, selectedSupervisor, effectiveAgents, preferences.pageSize);
  const summaryData = useSummaryData(
    dateRange,
    selectedSupervisor,
    effectiveAgents,
    pagination,
    preferences,
    null,
    agentsNone
  );
  const selectedJobNames = useMemo(() => selection.getSelectedJobNames(), [selection.totalSelectedCount]); // eslint-disable-line react-hooks/exhaustive-deps
  const summaryGeneration = useSummaryGeneration(
    summaryName,
    dateRange,
    selectedJobNames,
    selection.totalSelectedCount,
    summaryData.tableItems,
    selectedSupervisor,
    effectiveAgents,
    selection.allFiltered
  );

  // feature 025 (US8, T054): «todas las del filtro» sólo tiene sentido con rango y con llamadas, y
  // no con texto en el buscador: el backend lo ignora (revisión del PR, pregunta 1)
  const hasDateRange = Boolean(dateRange && dateRange.startDate && dateRange.endDate);
  const isAllFilteredButtonEnabled = isAllFilteredAvailable({
    hasDateRange,
    itemsCount: summaryData.tableItems.length,
    allFiltered: selection.allFiltered,
    filteringText,
  });

  // Si el modo ya estaba activo y se escribe en el buscador, se apaga: el resumen sería de todo el
  // filtro, no de lo que se ve
  const handleFilteringTextChange = useCallback((text) => {
    setFilteringText(text);
    if (hasSearchText(text) && selection.allFiltered) {
      selection.setAllFiltered(false);
    }
  }, [selection]);

  const pageSelectedItems = useMemo(
    () => selection.getPageSelectedItems(summaryData.tableItems),
    [summaryData.tableItems, selection.totalSelectedCount] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSelectionChange = useCallback((newPageSelected) => {
    selection.handleSelectionChange(newPageSelected, summaryData.tableItems);
  }, [selection, summaryData.tableItems]);

  // Handler para cambio de supervisor. SS-537: la selección de asesores se conserva; al llegar la
  // lista nueva se reconcilia (ver arriba)
  const handleSupervisorChange = (newSupervisor) => {
    setSelectedSupervisor(newSupervisor);
  };

  // Limpiar selección cross-page cuando cambian los filtros principales; el modo «todas las del
  // filtro» sobrevive (FR-045)
  useEffect(() => {
    selection.resetForFilterChange();
  }, [dateRange, selectedSupervisor, effectiveAgents, agentsNone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler para cambio de preferencias. Si cambia el tamaño, usePagination descarta los cursores
  // y vuelve a la página 1 (feature 025, hallazgo U2.3).
  const handlePreferencesChange = ({ detail }) => {
    setPreferences(detail);
  };

  // Handler para cambio de ordenación
  const handleSortingChange = ({ detail }) => {
    setSortingColumn(detail);
    setSortingDescending(detail.sortingDescending);
  };

  // Handler para cancelar
  const handleCancel = () => {
    history.push('/history-insights-calls');
  };

  // Función para obtener datos del insight desde la lambda get.js
  const fetchInsightData = useCallback(async (call) => {
    if (!call) {
      setCallDetails(null);
      setInsightError(null);
      return;
    }

    // Usar solo la key que devuelve el backend (igual que Lista de llamadas)
    const s3Key = call.key;
    if (!s3Key) {
      setInsightError(t("insights.errorLoadingInsight", { defaultValue: "Error al cargar la información del insight: No se pudo determinar el key del archivo" }));
      setCallDetails(null);
      return;
    }

    setLoadingDetails(true);
    setInsightError(null);
    setCallDetails(null);

    try {
      const response = await api.get(s3Key);

      // La respuesta puede ser un string JSON o un objeto
      let data;
      if (typeof response === 'string') {
        data = JSON.parse(response);
      } else {
        data = response;
      }

      // Guardar los datos completos (no solo ConversationAnalytics)
      setCallDetails(data);
      setInsightError(null);
    } catch (error) {
      console.error("Error fetching insight data:", error);
      setInsightError(
        t("insights.errorLoadingInsight", {
          defaultValue: "Error al cargar la información del insight"
        }) + `: ${error.message || error.toString()}`
      );
      setCallDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  }, [t]);

  // Handler para ver insight
  const handleViewInsight = (item) => {
    setSelectedCall(item);
    setActiveTabId("info");
    setCallDetails(null);
    setInsightError(null);

    // Abrir el panel inmediatamente con contenido de carga
    const loadingPanelContent = (
      <InsightDetailPanel
        selectedCall={item}
        callDetails={null}
        loadingDetails={true}
        insightError={null}
        activeTabId="info"
        setActiveTabId={setActiveTabId}
        history={history}
        t={t}
      />
    );

    // No pasar título, el header se maneja dinámicamente desde InsightDetailPanel
    openSplitPanel(loadingPanelContent, '');

    // Cargar datos del insight
    fetchInsightData(item);
  };

  // Actualizar el panel cuando cambian los datos
  useEffect(() => {
    if (selectedCall) {
      const panelContent = (
        <InsightDetailPanel
          selectedCall={selectedCall}
          callDetails={callDetails}
          loadingDetails={loadingDetails}
          insightError={insightError}
          activeTabId={activeTabId}
          setActiveTabId={setActiveTabId}
          history={history}
          t={t}
        />
      );

      // No pasar título, el header se maneja dinámicamente desde InsightDetailPanel
      openSplitPanel(panelContent, '');
    } else {
      closeSplitPanel();
    }
  }, [selectedCall, callDetails, activeTabId, loadingDetails, insightError, openSplitPanel, closeSplitPanel, history, t]);

  // Limpiar selectedCall cuando el panel se cierra
  useEffect(() => {
    if (!splitPanelOpen && selectedCall) {
      // Solo limpiar si el panel se cerró explícitamente (no durante la carga inicial)
      // Usar un pequeño delay para evitar conflictos con la apertura del panel
      const timeoutId = setTimeout(() => {
        if (!splitPanelOpen) {
          setSelectedCall(null);
          setCallDetails(null);
          setActiveTabId("info");
          setInsightError(null);
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [splitPanelOpen, selectedCall]);

  // Calcular pagesCount basándose en si hay más páginas
  const pagesCount = pagination.hasMorePages
    ? pagination.currentPageIndex + 1
    : pagination.currentPageIndex;

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("insights.newCXSummaryDescription")}
        >
          {t("insights.newCXSummaryTitle")}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <SummaryConfigurationForm
          summaryName={summaryName}
          dateRange={dateRange}
          selectedSupervisor={selectedSupervisor}
          selectedAgents={selectedAgents}
          supervisors={supervisors.supervisors}
          agents={agents.agents}
          supervisorsEnabled={supervisors.supervisorsEnabled}
          agentsEnabled={agents.agentsEnabled}
          onSummaryNameChange={setSummaryName}
          onDateRangeChange={setDateRange}
          onSupervisorChange={handleSupervisorChange}
          onAgentsChange={setSelectedAgents}
          getAgentsPlaceholder={agents.getAgentsPlaceholder}
          t={t}
        />
        
        {dateRange && dateRange.startDate && dateRange.endDate && (
          <SummaryTable
            items={summaryData.tableItems}
            loading={summaryData.loading}
            selectedItems={pageSelectedItems}
            onSelectionChange={handleSelectionChange}
            totalSelectedCount={selection.totalSelectedCount}
            allFiltered={selection.allFiltered}
            filteringText={filteringText}
            onFilteringTextChange={handleFilteringTextChange}
            currentPageIndex={pagination.currentPageIndex}
            pagesCount={pagesCount}
            onPageChange={pagination.setCurrentPageIndex}
            preferences={preferences}
            onPreferencesChange={handlePreferencesChange}
            sortingColumn={sortingColumn}
            sortingDescending={sortingDescending}
            onSortingChange={handleSortingChange}
            dateRange={dateRange}
            onViewInsight={handleViewInsight}
            t={t}
          />
        )}

        <Box float="right" padding={{ bottom: "l" }}>
          <SpaceBetween direction="horizontal" size="m">
            <Button
              variant="link"
              onClick={handleCancel}
            >
              {t("insights.cancel")}
            </Button>
            <span
              title={hasSearchText(filteringText)
                ? t("insights.allFilteredSearchHint", {
                    defaultValue: "Vaciá el buscador para resumir todas las llamadas del filtro",
                  })
                : undefined}
            >
              <Button
                variant="normal"
                onClick={() => selection.setAllFiltered(true)}
                disabled={!isAllFilteredButtonEnabled}
              >
                {t("insights.selectAllFiltered", { defaultValue: "Todas las del filtro" })}
              </Button>
            </span>
            <Button
              variant="normal"
              onClick={summaryGeneration.generateSummary}
              disabled={!summaryGeneration.isCreateSummaryButtonEnabled()}
              loading={summaryGeneration.generatingSummary}
            >
              {t("insights.createSummary")}
            </Button>
          </SpaceBetween>
        </Box>
        {/* Empty space added to display date picker correctly */}
        {!(dateRange && dateRange.startDate && dateRange.endDate) && (
          <div style={{ height: '400px' }}>
          </div>
        )}

      </SpaceBetween>

      <ResultModal
        visible={summaryGeneration.showPostSuccessModal}
        onDismiss={() => summaryGeneration.setShowPostSuccessModal(false)}
        result={summaryGeneration.postRequestResult}
        t={t}
      />

      <ConfirmationModal
        visible={summaryGeneration.showConfirmationModal}
        onDismiss={() => summaryGeneration.setShowConfirmationModal(false)}
        onConfirm={summaryGeneration.processAfterConfirmation}
        summaryName={summaryName}
        dateRange={dateRange}
        selectedSupervisor={selectedSupervisor}
        selectedAgents={effectiveAgents}
        verificationResult={summaryGeneration.verificationResult}
        generatingSummary={summaryGeneration.generatingSummary}
        t={t}
      />
    </ContentLayout>
  );
}

export default InsightsCalls; 
